import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaUserShield, FaUserTie, FaUserPlus } from 'react-icons/fa';
import { adminDashboardAPI, adminProductAPI, adminSeoAPI } from '../services/api';
import { useAlert } from '../context/AlertContext';
import '../styles/Dashboard.css';

const SEO_PAGES = [
  { key: 'home',        label: 'Home',           group: 'Static' },
  { key: 'about',       label: 'About Us',        group: 'Static' },
  { key: 'contact',     label: 'Contact',         group: 'Static' },
  { key: 'services',    label: 'Services',        group: 'Static' },
  { key: 'products',    label: 'Products',        group: 'Static' },
  { key: 'blogs',       label: 'Blogs',           group: 'Static' },
  { key: 'promotions',  label: 'Promotions',      group: 'Static' },
  { key: 'product-category-antibodies',                    label: 'Antibodies',          group: 'Category' },
  { key: 'product-category-elisa-kits-multiplex-panels',   label: 'ELISA Kits',          group: 'Category' },
  { key: 'product-category-cell-lines-and-culture-products', label: 'Cell Lines',         group: 'Category' },
  { key: 'product-category-biochemicals',                  label: 'Biochemicals',        group: 'Category' },
  { key: 'product-category-proteins-and-peptides',         label: 'Proteins & Peptides', group: 'Category' },
  { key: 'product-category-nucleic-acid-extraction-kits',  label: 'Nucleic Acid Kits',   group: 'Category' },
  { key: 'product-category-nanoparticles-beads',           label: 'Nanoparticles & Beads', group: 'Category' },
  { key: 'product-category-assay-kits',                    label: 'Assay Kits',          group: 'Category' },
  { key: 'product-category-molecular-biology-products',    label: 'Molecular Biology',   group: 'Category' },
  { key: 'product-category-plastic-wares',                 label: 'Plastic Wares',       group: 'Category' },
];

const GATE_KEY = 'ecbiolabs_dashboard_unlocked';
const CORRECT_PASSWORD = 'qwert!@#123';

const Dashboard = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem(GATE_KEY) === 'true');
  const [gatePassword, setGatePassword] = useState('');
  const [gateError, setGateError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [shaking, setShaking] = useState(false);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clearConfirm, setClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearMessage, setClearMessage] = useState(null);

  // SEO Quick Panel
  const [showSeoPanel, setShowSeoPanel] = useState(false);
  const [seoSelectedKey, setSeoSelectedKey] = useState(SEO_PAGES[0].key);
  const [seoForm, setSeoForm] = useState({
    metaTitle: '', metaDescription: '',
    ogTitle: '', ogDescription: '', ogImage: '',
    robots: 'index, follow', canonicalOverride: '', schemaJson: '',
  });
  const [seoLoading, setSeoLoading] = useState(false);
  const [seoSaving, setSeoSaving] = useState(false);
  const [showSeoOg, setShowSeoOg] = useState(false);
  const [showSeoAdvanced, setShowSeoAdvanced] = useState(false);
  const [seoSchemaError, setSeoSchemaError] = useState('');

  useEffect(() => {
    if (unlocked) {
      fetchDashboardStats();
    }
  }, [unlocked]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await adminDashboardAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      setError('Failed to load dashboard statistics');
      console.error('Dashboard stats error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRoleCount = (role) => {
    if (!stats?.usersByRole) return 0;
    const roleData = stats.usersByRole.find(r => r._id === role);
    return roleData?.count || 0;
  };

  const handleClearAll = async () => {
    setClearing(true);
    setClearMessage(null);
    try {
      await adminProductAPI.clearAll();
      setClearMessage({ type: 'success', text: 'All products cleared successfully.' });
    } catch (err) {
      setClearMessage({ type: 'error', text: err.response?.data?.message || 'Failed to clear products.' });
    } finally {
      setClearing(false);
      setClearConfirm(false);
    }
  };

  const handleGateSubmit = (e) => {
    e.preventDefault();
    if (gatePassword === CORRECT_PASSWORD) {
      sessionStorage.setItem(GATE_KEY, 'true');
      setUnlocked(true);
    } else {
      setGateError('Incorrect password. Please try again.');
      setShaking(true);
      setGatePassword('');
      setTimeout(() => setShaking(false), 600);
    }
  };

  const fetchSeoPage = useCallback(async (pageKey) => {
    setSeoLoading(true);
    setSeoSchemaError('');
    try {
      const res = await adminSeoAPI.getByPageKey(pageKey);
      const data = res.data.data || {};
      setSeoForm({
        metaTitle:        data.metaTitle        || '',
        metaDescription:  data.metaDescription  || '',
        ogTitle:          data.ogTitle          || '',
        ogDescription:    data.ogDescription    || '',
        ogImage:          data.ogImage          || '',
        robots:           data.robots           || 'index, follow',
        canonicalOverride: data.canonicalOverride || '',
        schemaJson:       data.schemaJson       || '',
      });
    } catch {
      setSeoForm({ metaTitle: '', metaDescription: '', ogTitle: '', ogDescription: '', ogImage: '', robots: 'index, follow', canonicalOverride: '', schemaJson: '' });
    } finally {
      setSeoLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showSeoPanel) fetchSeoPage(seoSelectedKey);
  }, [seoSelectedKey, showSeoPanel, fetchSeoPage]);

  const validateSeoSchema = () => {
    if (!seoForm.schemaJson.trim()) return true;
    try {
      JSON.parse(seoForm.schemaJson);
      return true;
    } catch {
      setSeoSchemaError('Invalid JSON — please check your schema syntax.');
      return false;
    }
  };

  const handleSeoSave = async (e) => {
    e.preventDefault();
    if (!validateSeoSchema()) return;
    setSeoSaving(true);
    try {
      const page = SEO_PAGES.find(p => p.key === seoSelectedKey);
      await adminSeoAPI.upsert(seoSelectedKey, { ...seoForm, pageLabel: page?.label });
      showAlert('SEO settings saved!', 'success');
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setSeoSaving(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="dashboard-gate-overlay">
        <div className={`dashboard-gate-box${shaking ? ' shake' : ''}`}>
          <div className="dashboard-gate-icon">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
          <h2>Dashboard Access</h2>
          <p>Enter the password to view the dashboard</p>
          <form onSubmit={handleGateSubmit} className="dashboard-gate-form">
            <div className="dashboard-gate-input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                value={gatePassword}
                onChange={(e) => { setGatePassword(e.target.value); setGateError(''); }}
                placeholder="Enter password"
                autoFocus
                autoComplete="off"
              />
              <button
                type="button"
                className="dashboard-gate-eye"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {gateError && <p className="dashboard-gate-error">{gateError}</p>}
            <button type="submit" className="dashboard-gate-btn">Unlock</button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Welcome to ECBioLabs Admin Dashboard</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-icon">
            <FaUsers />
          </div>
          <div className="stat-content">
            <h3>Total Users</h3>
            <p className="stat-number">{stats?.totalUsers || 0}</p>
          </div>
        </div>

        <div className="stat-card green">
          <div className="stat-icon">
            <FaUserShield />
          </div>
          <div className="stat-content">
            <h3>Admins</h3>
            <p className="stat-number">{getRoleCount('admin')}</p>
          </div>
        </div>

        <div className="stat-card purple">
          <div className="stat-icon">
            <FaUserTie />
          </div>
          <div className="stat-content">
            <h3>Moderators</h3>
            <p className="stat-number">{getRoleCount('moderator')}</p>
          </div>
        </div>

        <div className="stat-card orange">
          <div className="stat-icon">
            <FaUserPlus />
          </div>
          <div className="stat-content">
            <h3>New This Week</h3>
            <p className="stat-number">{stats?.recentUsers || 0}</p>
          </div>
        </div>
      </div>

      {/* ── SEO Quick Settings Toggle ── */}
      <div className="dash-seo-panel">
        <button
          className="dash-seo-toggle"
          onClick={() => setShowSeoPanel(prev => !prev)}
        >
          <div className="dash-seo-toggle-left">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <span>SEO Quick Settings</span>
            <span className="dash-seo-badge">{SEO_PAGES.length} pages</span>
          </div>
          <div className="dash-seo-toggle-right">
            <button
              type="button"
              className="dash-seo-fulllink"
              onClick={(e) => { e.stopPropagation(); navigate('/seo-settings'); }}
            >
              Full Settings →
            </button>
            <span className={`dash-seo-chevron ${showSeoPanel ? 'open' : ''}`}>&#8250;</span>
          </div>
        </button>

        {showSeoPanel && (
          <div className="dash-seo-body">
            <div className="dash-seo-layout">

              {/* Left — page tabs */}
              <div className="dash-seo-pagelist">
                <p className="dash-seo-group-label">Static Pages</p>
                {SEO_PAGES.filter(p => p.group === 'Static').map(page => (
                  <button
                    key={page.key}
                    className={`dash-seo-pageitem ${seoSelectedKey === page.key ? 'active' : ''}`}
                    onClick={() => setSeoSelectedKey(page.key)}
                  >
                    {page.label}
                  </button>
                ))}
                <p className="dash-seo-group-label" style={{ marginTop: '0.5rem' }}>Product Categories</p>
                {SEO_PAGES.filter(p => p.group === 'Category').map(page => (
                  <button
                    key={page.key}
                    className={`dash-seo-pageitem ${seoSelectedKey === page.key ? 'active' : ''}`}
                    onClick={() => setSeoSelectedKey(page.key)}
                  >
                    {page.label}
                  </button>
                ))}
              </div>

              {/* Right — form */}
              <div className="dash-seo-form-area">
                {seoLoading ? (
                  <p className="dash-seo-loading">Loading...</p>
                ) : (
                  <form onSubmit={handleSeoSave}>
                    <p className="dash-seo-form-page">
                      {SEO_PAGES.find(p => p.key === seoSelectedKey)?.label}
                    </p>
                    <div className="dash-seo-field">
                      <div className="dash-seo-field-header">
                        <label>Meta Title</label>
                        <span className={`dash-seo-count ${seoForm.metaTitle.length > 60 ? 'warn' : ''}`}>
                          {seoForm.metaTitle.length}/70
                        </span>
                      </div>
                      <input
                        type="text"
                        value={seoForm.metaTitle}
                        onChange={e => setSeoForm(f => ({ ...f, metaTitle: e.target.value }))}
                        maxLength={70}
                        placeholder="Page title for search engines"
                      />
                    </div>
                    <div className="dash-seo-field">
                      <div className="dash-seo-field-header">
                        <label>Meta Description</label>
                        <span className={`dash-seo-count ${seoForm.metaDescription.length > 180 ? 'warn' : ''}`}>
                          {seoForm.metaDescription.length}/200
                        </span>
                      </div>
                      <textarea
                        value={seoForm.metaDescription}
                        onChange={e => setSeoForm(f => ({ ...f, metaDescription: e.target.value }))}
                        maxLength={200}
                        rows={3}
                        placeholder="Short description shown in search results"
                      />
                    </div>
                    {/* ── Open Graph Toggle ── */}
                    <div className="dash-seo-section">
                      <button
                        type="button"
                        className="dash-seo-section-toggle"
                        onClick={() => setShowSeoOg(prev => !prev)}
                      >
                        <span>Open Graph (Social Sharing)</span>
                        <span className={`dash-seo-section-icon ${showSeoOg ? 'open' : ''}`}>&#8250;</span>
                      </button>
                      {showSeoOg && (
                        <div className="dash-seo-section-body">
                          <div className="dash-seo-field">
                            <label>OG Title</label>
                            <input
                              type="text"
                              value={seoForm.ogTitle}
                              onChange={e => setSeoForm(f => ({ ...f, ogTitle: e.target.value }))}
                              maxLength={100}
                              placeholder="Defaults to Meta Title if left blank"
                            />
                          </div>
                          <div className="dash-seo-field">
                            <label>OG Description</label>
                            <input
                              type="text"
                              value={seoForm.ogDescription}
                              onChange={e => setSeoForm(f => ({ ...f, ogDescription: e.target.value }))}
                              maxLength={200}
                              placeholder="Defaults to Meta Description if left blank"
                            />
                          </div>
                          <div className="dash-seo-field">
                            <label>OG Image URL</label>
                            <input
                              type="url"
                              value={seoForm.ogImage}
                              onChange={e => setSeoForm(f => ({ ...f, ogImage: e.target.value }))}
                              placeholder="https://ecbiolabs.com/og-image.jpg (1200×630px)"
                            />
                            {seoForm.ogImage && (
                              <div className="dash-seo-og-preview">
                                <img src={seoForm.ogImage} alt="OG preview" onError={e => e.target.style.display = 'none'} />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ── Advanced SEO Toggle ── */}
                    <div className="dash-seo-section">
                      <button
                        type="button"
                        className="dash-seo-section-toggle"
                        onClick={() => setShowSeoAdvanced(prev => !prev)}
                      >
                        <span>Advanced SEO Settings</span>
                        <span className={`dash-seo-section-icon ${showSeoAdvanced ? 'open' : ''}`}>&#8250;</span>
                      </button>
                      {showSeoAdvanced && (
                        <div className="dash-seo-section-body">
                          <div className="dash-seo-field">
                            <label>Robots</label>
                            <select
                              value={seoForm.robots}
                              onChange={e => setSeoForm(f => ({ ...f, robots: e.target.value }))}
                            >
                              <option value="index, follow">index, follow (default)</option>
                              <option value="noindex, nofollow">noindex, nofollow</option>
                              <option value="index, nofollow">index, nofollow</option>
                            </select>
                          </div>
                          <div className="dash-seo-field">
                            <label>Canonical URL Override</label>
                            <input
                              type="url"
                              value={seoForm.canonicalOverride}
                              onChange={e => setSeoForm(f => ({ ...f, canonicalOverride: e.target.value }))}
                              placeholder="Leave blank to use default canonical"
                            />
                          </div>
                          <div className="dash-seo-field">
                            <label>Custom JSON-LD Schema</label>
                            <textarea
                              value={seoForm.schemaJson}
                              onChange={e => { setSeoForm(f => ({ ...f, schemaJson: e.target.value })); setSeoSchemaError(''); }}
                              rows={5}
                              placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "WebPage"\n}'}
                              className={seoSchemaError ? 'error' : ''}
                            />
                            {seoSchemaError && <small className="dash-seo-error-text">{seoSchemaError}</small>}
                            <small className="dash-seo-hint">Valid JSON-LD only. Leave blank to use default.</small>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="dash-seo-form-actions">
                      <button type="submit" className="dash-seo-save-btn" disabled={seoSaving}>
                        {seoSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="dash-seo-full-btn"
                        onClick={() => navigate('/seo-settings')}
                      >
                        Open Full Editor
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="danger-zone-section">
        <h2>Danger Zone</h2>
        {clearMessage && (
          <div className={`clear-message ${clearMessage.type}`}>
            {clearMessage.text}
          </div>
        )}
        {!clearConfirm ? (
          <button className="clear-all-btn" onClick={() => { setClearConfirm(true); setClearMessage(null); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Clear All Products
          </button>
        ) : (
          <div className="clear-confirm-box">
            <p>This will permanently delete <strong>all products</strong>. This action cannot be undone.</p>
            <div className="clear-confirm-actions">
              <button className="clear-confirm-btn" onClick={handleClearAll} disabled={clearing}>
                {clearing ? 'Clearing...' : 'Yes, Clear All'}
              </button>
              <button className="clear-cancel-btn" onClick={() => setClearConfirm(false)} disabled={clearing}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="recent-users-section">
        <h2>Recent Users</h2>
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {stats?.latestUsers?.length > 0 ? (
                stats.latestUsers.map((user) => (
                  <tr key={user._id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge ${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="no-data">No recent users</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
